import React from "react";
import clsx from "clsx";
import { ChevronDown, Search } from "lucide-react";
import {
  Button,
  ButtonVariant,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Text,
  TextField,
  Tooltip,
} from "@merge-api/merge-javascript-shared";
import {
  SCOPES_ACTION_STATE_ANY,
  SCOPES_ACTION_STATE_OPTIONAL,
  SCOPES_ACTION_STATE_OPTIONS,
  SCOPES_SORT_OPTIONS,
} from "../constants";
import { ScopesToolbar as ScopesToolbarState } from "../hooks/useScopesToolbar";

// Filtering/sorting mid-edit could hide a row that still has an unsaved change, which would then be
// saved unseen, so the controls are locked while editing.
const EDIT_DISABLED_TOOLTIP = "Finish or cancel editing to search or filter";

interface Props {
  categoryLabel: string;
  toolbar: ScopesToolbarState;
  /** When false, the "Optional" filter option is hidden (picking it would always return nothing). */
  areOptionalScopesEnabled: boolean;
  /** Locks Search / Filter / Sort while editing so a pending edit can't be filtered out of view. */
  disabled: boolean;
}

/**
 * Sort / Filter / Search controls for the Common Model scopes table. Sort is a single-select menu;
 * Filter is a stay-open menu with two columns (Read / Write), each a single-select of
 * Any / Enabled / Optional / Disabled; Search is debounced inside useScopesToolbar. All drive
 * server-side filtering via that hook.
 */
const ScopesToolbar = ({ categoryLabel, toolbar, areOptionalScopesEnabled, disabled }: Props) => {
  const { search, setSearch, ordering, setOrdering, read, setRead, write, setWrite } = toolbar;
  const tooltip = disabled ? EDIT_DISABLED_TOOLTIP : "";

  // derived state
  const sortLabel =
    SCOPES_SORT_OPTIONS.find((option) => option.value === ordering)?.label ??
    SCOPES_SORT_OPTIONS[0].label;
  // Hide the "Optional" option unless the org has optional scopes enabled.
  const filterOptions = areOptionalScopesEnabled
    ? SCOPES_ACTION_STATE_OPTIONS
    : SCOPES_ACTION_STATE_OPTIONS.filter((option) => option.value !== SCOPES_ACTION_STATE_OPTIONAL);
  // Count of directions actively filtered (Read and/or Write set to something other than "Any").
  const filterCount =
    (read !== SCOPES_ACTION_STATE_ANY ? 1 : 0) + (write !== SCOPES_ACTION_STATE_ANY ? 1 : 0);

  // render
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-4 lg:flex-1">
      <Tooltip title={tooltip}>
        <Menu
          menuPlacement="bottom-start"
          ButtonProps={{
            variant: ButtonVariant.TertiaryWhite,
            rightIcon: <ChevronDown size={16} />,
            children: `Sort by: ${sortLabel}`,
            disabled,
          }}
        >
          {SCOPES_SORT_OPTIONS.map((option) => (
            <MenuItem key={option.value} onClick={() => setOrdering(option.value)}>
              <Text>{option.label}</Text>
            </MenuItem>
          ))}
        </Menu>
      </Tooltip>

      <Tooltip title={tooltip}>
        <Menu
          menuPlacement="bottom-start"
          ButtonProps={{
            variant: ButtonVariant.TertiaryWhite,
            children: filterCount > 0 ? `Filter (${filterCount})` : "Filter",
            disabled,
          }}
        >
          {/* Two columns of radios (not MenuItems) so clicking a radio keeps the menu open. */}
          <div className="flex gap-14 px-5 py-4">
            <div className="flex min-w-[128px] flex-col gap-4">
              <Text variant="h6">Read</Text>
              <RadioGroup value={read} onValueChange={setRead} className="flex flex-col gap-3">
                {filterOptions.map((option) => (
                  <Radio key={option.value} value={option.value} label={option.label} />
                ))}
              </RadioGroup>
            </div>
            <div className="flex min-w-[128px] flex-col gap-4">
              <Text variant="h6">Write</Text>
              <RadioGroup value={write} onValueChange={setWrite} className="flex flex-col gap-3">
                {filterOptions.map((option) => (
                  <Radio key={option.value} value={option.value} label={option.label} />
                ))}
              </RadioGroup>
            </div>
          </div>
        </Menu>
      </Tooltip>

      <Tooltip title={tooltip} className="w-full lg:max-w-[400px] lg:flex-1">
        <TextField
          className="w-full"
          placeholder={`Search ${categoryLabel} Scopes and fields`}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          disabled={disabled}
          // 12px before the icon; inputClassName drops the input's left pad to 8px for the icon-to-text gap.
          // The prefix sits outside the input, so it needs the disabled background applied itself.
          prefix={
            <div
              className={clsx(
                "flex h-full items-center rounded-l-md pl-3",
                disabled && "bg-gray-10",
              )}
            >
              <Search size={16} className="text-gray-40" />
            </div>
          }
          inputClassName="pl-2"
        />
      </Tooltip>
    </div>
  );
};

export default ScopesToolbar;
